import { EventProgramHandle, RuntimeStateHandle } from 'fluveria-engine-wasm';
import type {
  EventDispatchResult,
  EventGraphDef,
  EventRecordContext,
  RuntimeStateSchema,
} from '../graph/eventGraph.js';
import { WasmProgramCache } from './wasmProgramCache.js';

/**
 * Owns live Event Graph execution for one editor session. It deliberately has
 * no dependency on command history or persistence: a dispatch only mutates
 * the WASM RuntimeState handle and reports the affected declared keys.
 */
export class EventRuntimeManager {
  private schemaSignature = '';
  private state: RuntimeStateHandle | null = null;
  private readonly programs = new WasmProgramCache<EventProgramHandle>();
  lastError: string | null = null;
  lastChangedKeys: string[] = [];
  private readonly listeners = new Set<(keys: readonly string[]) => void>();

  subscribeChanged(listener: (keys: readonly string[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  syncSchema(schema: RuntimeStateSchema): void {
    const signature = JSON.stringify(schema);
    if (signature === this.schemaSignature) return;
    this.schemaSignature = signature;
    this.state?.free();
    this.state = new RuntimeStateHandle(schema);
    this.programs.clear();
    this.lastError = null;
    this.lastChangedKeys = [];
  }

  dispatch(
    graphId: string,
    graph: EventGraphDef,
    name: string,
    payload: Record<string, unknown> = {},
    ownerKey?: string,
    recordContext?: EventRecordContext,
  ): EventDispatchResult {
    try {
      const state = this.requireState();
      const program = this.program(graphId, graph, state);
      const result = program.dispatch(state, { name, payload, ownerKey, recordContext }) as EventDispatchResult;
      this.lastError = null;
      this.lastChangedKeys = result.changedKeys;
      if (result.changedKeys.length) for (const listener of this.listeners) listener(result.changedKeys);
      return result;
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      this.lastChangedKeys = [];
      throw error;
    }
  }

  reset(): void {
    this.requireState().reset();
    this.lastError = null;
    this.lastChangedKeys = [];
    for (const listener of this.listeners) listener(Object.keys(this.inspectorSnapshot().schema));
  }

  exportSnapshot(): unknown {
    return this.requireState().exportSnapshot();
  }

  restoreSnapshot(snapshot: unknown): void {
    this.requireState().restoreSnapshot(snapshot);
    for (const listener of this.listeners) listener(Object.keys(this.inspectorSnapshot().schema));
  }

  handle(): RuntimeStateHandle {
    return this.requireState();
  }

  /**
   * Payload field names an event declares, or undefined when the graph has no
   * such event. A payload is validated for an exact key match, so a caller that
   * guesses a field fails the whole dispatch — build the payload from this.
   */
  eventFields(graphId: string, graph: EventGraphDef, name: string): string[] | undefined {
    const program = this.program(graphId, graph, this.requireState());
    return program.eventFields(name) as string[] | undefined;
  }

  inspectorSnapshot(ownerKey?: string): { schema: RuntimeStateSchema; global: Record<string, unknown>; ownerKey?: string; owner?: Record<string, unknown> } {
    return this.requireState().inspectorSnapshot(ownerKey) as { schema: RuntimeStateSchema; global: Record<string, unknown>; ownerKey?: string; owner?: Record<string, unknown> };
  }

  dispose(): void {
    this.state?.free();
    this.state = null;
    this.programs.clear();
  }

  private requireState(): RuntimeStateHandle {
    if (!this.state) this.syncSchema({});
    return this.state!;
  }

  private program(graphId: string, graph: EventGraphDef, state: RuntimeStateHandle): EventProgramHandle {
    return this.programs.get(graphId, graph, () => new EventProgramHandle(graph, state));
  }
}
